import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import useSWR from 'swr';

import { IAddress } from '@modules/fedex/Addresses/interfaces';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';

import { IDropdownData, IReadyToShip } from '../interfaces';
import { readyToShipValidationSchema } from '../validation';
import { ReadyToShipForm } from './ReadyToShipForm';

interface IReadyToShipModalProps {
  modalIsOpen: boolean;
  handleSubmit: (data: IReadyToShip) => Promise<void>;
  currentReadyToShip: IReadyToShip | null;
  handleClose: () => void;
  currentReadyToShipPosition: number;
  loading: boolean;
}

export const ReadyToShipModal = ({
  modalIsOpen,
  handleSubmit,
  currentReadyToShip,
  currentReadyToShipPosition,
  loading,
  handleClose,
}: IReadyToShipModalProps) => {
  const { data: dropdownData } = useSWR<IDropdownData>(`${SERVICES.DOCUMENT}/shipping/provider/servicetype`);
  const { data: shippingAddresses = [] } = useSWR<IAddress[]>(`${SERVICES.DOCUMENT}/shipping-address`);

  const shipmentServiceTypes = useMemo(
    () =>
      (dropdownData?.shipmentServiceTypes ?? []).map((serviceType) => ({
        value: serviceType,
        label: serviceType,
      })),
    [dropdownData],
  );

  const initialReadyToShipState: IReadyToShip = useMemo(() => ({
    shipmentAddressProfileId: currentReadyToShip?.shipmentAddressProfileId || null,
    name: currentReadyToShip?.name || '',
    nickName: currentReadyToShip?.nickName || '',
    company: currentReadyToShip?.company || '',
    address: currentReadyToShip?.address || '',
    address2: currentReadyToShip?.address2 || '',
    city: currentReadyToShip?.city || '',
    state: currentReadyToShip?.state || '',
    zip: currentReadyToShip?.zip || '',
    phoneNumber: currentReadyToShip?.phoneNumber || '',
    isReturnLabelChecked: currentReadyToShip?.isReturnLabelChecked || false,
    dealsInThisBatch: currentReadyToShip?.dealsInThisBatch || [],
    serviceType: currentReadyToShip?.serviceType || 'STANDARD_OVERNIGHT',
    userId: currentReadyToShip?.userId || '',
  }), [currentReadyToShip]);

  return (
    <Modal
      hideBackdrop
      title="Create Shipment"
      open={modalIsOpen}
      dialogWidth="700px"
      resetPosition={currentReadyToShipPosition}
      onClose={handleClose}
    >
      <Formik
        enableReinitialize
        initialValues={initialReadyToShipState as IReadyToShip}
        validationSchema={readyToShipValidationSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit}>
            <ReadyToShipForm
              handleClose={handleClose}
              loading={loading}
              shipmentServiceTypes={shipmentServiceTypes}
              shippingAddresses={shippingAddresses}
              {...props}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
